import type {
  CentinelaNotificationChannel,
  CentinelaSchedule,
  CentinelaSupervisor,
  CentinelaSupervisoresResponse,
  CentinelaUser,
  CentinelaUserNotifications,
  CentinelaUsersResponse,
} from "../../../features/centinela/types";
import type { NotificationChannelOption, NotificationUser } from "../types";

const CHANNEL_ICONS: Record<string, string> = {
  notificacion_push: "ri-notification-3-line",
  email: "ri-mail-line",
  llamada: "ri-phone-line",
  sms: "ri-message-2-line",
  whatsapp: "ri-whatsapp-line",
};

const FALLBACK_CHANNEL_ICON = "ri-send-plane-line";

export const channelIcon = (id: string): string => CHANNEL_ICONS[id] ?? FALLBACK_CHANNEL_ICON;

const PHONE_REQUIREMENTS = ["telefono", "teléfono", "phone"];

const normalizeRequirement = (value: string | null | undefined): string =>
  (value ?? "").normalize("NFC").trim().toLowerCase();

const requiresPhone = (value: string | null | undefined): boolean => {
  const requirement = normalizeRequirement(value);
  return requirement.length > 0 && PHONE_REQUIREMENTS.some((need) => requirement.includes(need));
};

const hasNoPhone = (user: CentinelaUser): boolean =>
  (user.telefono ?? "").trim().length === 0 || Boolean(user.sin_telefono);

function toChannelOptions(
  user: CentinelaUser,
  channels: CentinelaNotificationChannel[],
): NotificationChannelOption[] {
  const possible = user.canales_posibles ?? [];
  const selected = user.notificaciones?.canales ?? [];
  const noPhone = hasNoPhone(user);

  return channels.map((channel) => {
    const blockedByPhone = noPhone && requiresPhone(channel.requiere);
    const available = possible.includes(channel.id) && !blockedByPhone;
    return {
      id: channel.id,
      label: channel.label,
      icon: channelIcon(channel.id),
      selected: selected.includes(channel.id),
      available,
      requires: channel.requiere,
      unavailableReason: available ? null : (user.sin_telefono ?? null),
    };
  });
}

export function indexSupervisors(
  response: CentinelaSupervisoresResponse | undefined,
): Map<string, CentinelaSupervisor> {
  return new Map(
    (response?.supervisores ?? []).map((supervisor) => [supervisor.user_id, supervisor]),
  );
}

export function mapCentinelaUser(
  user: CentinelaUser,
  channels: CentinelaNotificationChannel[],
  roleNameOf: (roleId: string | null) => string | null,
  supervisor?: CentinelaSupervisor,
): NotificationUser {
  return {
    userId: user.user_id,
    name: user.nombre ?? user.email ?? user.user_id,
    email: user.email ?? null,
    phone: user.telefono ?? null,
    roleId: user.role_id ?? null,
    roleName: roleNameOf(user.role_id ?? null),
    active: user.activo !== false,
    enabled: user.notificaciones?.habilitado === true,
    noPhoneReason: user.sin_telefono ?? null,
    channels: toChannelOptions(user, channels),
    types: user.notificaciones?.tipos ?? [],
    schedule: supervisor?.horario ?? user.notificaciones?.horario ?? null,
    onShift: supervisor?.en_turno_ahora ?? null,
    shiftReason: supervisor?.motivo ?? null,
  };
}

export function mapCentinelaUsers(
  response: CentinelaUsersResponse | undefined,
  roleNameOf: (roleId: string | null) => string | null,
  supervisors?: Map<string, CentinelaSupervisor>,
): NotificationUser[] {
  const channels = response?.canales ?? [];
  return (response?.users ?? []).map((user) =>
    mapCentinelaUser(user, channels, roleNameOf, supervisors?.get(user.user_id)),
  );
}

const availableChannelsFor = (user: NotificationUser): string[] =>
  user.channels.filter((channel) => channel.available).map((channel) => channel.id);

export function toNotificationsPayload(
  user: NotificationUser,
  changes: { enabled?: boolean; channels?: string[]; schedule?: CentinelaSchedule },
): CentinelaUserNotifications {
  const blocked = new Set(user.channels.filter((c) => !c.available).map((c) => c.id));
  const selected =
    changes.channels ?? user.channels.filter((c) => c.selected).map((c) => c.id);
  const canales = selected.filter((id) => !blocked.has(id));
  const seedAvailable =
    changes.enabled === true && changes.channels === undefined && canales.length === 0;

  return {
    habilitado: changes.enabled ?? user.enabled,
    canales: seedAvailable ? availableChannelsFor(user) : canales,
    tipos: user.types,
    ...(changes.schedule ? { horario: changes.schedule } : {}),
  };
}
